import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import {
  ArrowLeftIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  SwatchIcon,
  ArrowPathIcon,
  CheckIcon,
  UserGroupIcon,
  EnvelopeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export default function AdminDashboard() {
  const { logout } = useAuth();
  const {
    settings,
    updateSettings,
    resetSettings,
    loading: settingsLoading,
    error: settingsError,
  } = useSettings();
  const [activeTab, setActiveTab] = useState<
    | "hero"
    | "cta"
    | "features"
    | "featurescards"
    | "howitworks"
    | "howitworkssteps"
    | "partners"
    | "institutions"
    | "institutionsfeatures"
    | "global"
    | "subscriptions"
    | "waitlist"
  >("hero");
  const [showSuccess, setShowSuccess] = useState(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [waitlist, setWaitlist] = useState<any[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleSave = async () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const fetchSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const response = await fetch("/api/subscriptions", {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (response.ok) {
        setSubscriptions(data.data);
      } else {
        console.error("Failed to fetch subscriptions:", data.error);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handleUnsubscribe = async (email: string) => {
    try {
      const response = await fetch("/api/unsubscribe", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubscriptions((prev) => prev.filter((sub) => sub.email !== email));
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else {
        console.error("Failed to unsubscribe email");
      }
    } catch (error) {
      console.error("Error unsubscribing email:", error);
    }
  };

  const fetchWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (response.ok) {
        setWaitlist(data.data);
      } else {
        console.error("Failed to fetch waitlist:", data.error);
      }
    } catch (error) {
      console.error("Error fetching waitlist:", error);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleDeleteWaitlistEntry = async (email: string) => {
    try {
      const response = await fetch(
        `/api/waitlist/${encodeURIComponent(email)}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setWaitlist((prev) => prev.filter((entry) => entry.email !== email));
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else {
        console.error("Failed to delete waitlist entry");
      }
    } catch (error) {
      console.error("Error deleting waitlist entry:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "subscriptions") {
      fetchSubscriptions();
    } else if (activeTab === "waitlist") {
      fetchWaitlist();
    }
  }, [activeTab]);

  const tabs = [
    { id: "hero", name: "Hero Section", icon: DocumentTextIcon },
    { id: "cta", name: "CTA Section", icon: PaintBrushIcon },
    { id: "features", name: "Features Section", icon: DocumentTextIcon },
    { id: "featurescards", name: "Features Cards", icon: DocumentTextIcon },
    { id: "howitworks", name: "How It Works", icon: PaintBrushIcon },
    {
      id: "howitworkssteps",
      name: "How It Works Steps",
      icon: DocumentTextIcon,
    },
    { id: "partners", name: "Partners Section", icon: DocumentTextIcon },
    {
      id: "institutions",
      name: "Institutions Section",
      icon: DocumentTextIcon,
    },
    {
      id: "institutionsfeatures",
      name: "Institutions Features",
      icon: DocumentTextIcon,
    },
    { id: "global", name: "Global Colors", icon: SwatchIcon },
    { id: "subscriptions", name: "Email Subscriptions", icon: EnvelopeIcon },
    { id: "waitlist", name: "Waitlist", icon: UserGroupIcon },
  ];

  // Show loading state while settings are being loaded
  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: settings.primaryColor }}
          ></div>
          <p className="text-gray-600 dark:text-gray-300">
            Loading settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Left Sidebar */}
      <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col fixed h-full">
        {/* Logo/Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-oxanium font-bold text-gray-900 dark:text-white">
            Admin Dashboard
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            de5.ai
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-3 space-y-1">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "text-white"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                style={
                  activeTab === tab.id
                    ? {
                        backgroundColor: settings.primaryColor,
                      }
                    : {}
                }
                whileHover={{ x: activeTab === tab.id ? 0 : 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <tab.icon className="w-5 h-5" />
                <span className="flex-1 text-left">{tab.name}</span>
              </motion.button>
            ))}
          </div>
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
          <motion.button
            onClick={resetSettings}
            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span>Reset Settings</span>
          </motion.button>
          <motion.button
            onClick={() => (window.location.href = "/")}
            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Site</span>
          </motion.button>
          <motion.button
            onClick={logout}
            className="w-full px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Logout
          </motion.button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {tabs.find((tab) => tab.id === activeTab)?.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Manage your website settings and content
              </p>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {/* Error Message */}
          {settingsError && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Error
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    {settingsError}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {tabs.find((tab) => tab.id === activeTab)?.name} Settings
              </h2>

              {activeTab === "hero" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hero Title
                    </label>
                    <input
                      type="text"
                      value={settings.heroTitle}
                      onChange={(e) =>
                        updateSettings({ heroTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hero Subtitle
                    </label>
                    <textarea
                      value={settings.heroSubtitle}
                      onChange={(e) =>
                        updateSettings({ heroSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.heroButtonText}
                      onChange={(e) =>
                        updateSettings({ heroButtonText: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Color
                      </label>
                      <input
                        type="color"
                        value={settings.heroButtonColor}
                        onChange={(e) =>
                          updateSettings({ heroButtonColor: e.target.value })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Text Color
                      </label>
                      <input
                        type="color"
                        value={settings.heroButtonTextColor}
                        onChange={(e) =>
                          updateSettings({
                            heroButtonTextColor: e.target.value,
                          })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Background Color
                    </label>
                    <input
                      type="text"
                      value={settings.heroBackgroundColor}
                      onChange={(e) =>
                        updateSettings({ heroBackgroundColor: e.target.value })
                      }
                      placeholder="e.g., rgba(249, 250, 251, 0.8)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                </div>
              )}

              {activeTab === "cta" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      CTA Title
                    </label>
                    <input
                      type="text"
                      value={settings.ctaTitle}
                      onChange={(e) =>
                        updateSettings({ ctaTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      CTA Subtitle
                    </label>
                    <textarea
                      value={settings.ctaSubtitle}
                      onChange={(e) =>
                        updateSettings({ ctaSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.ctaButtonText}
                      onChange={(e) =>
                        updateSettings({ ctaButtonText: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Color
                      </label>
                      <input
                        type="color"
                        value={settings.ctaButtonColor}
                        onChange={(e) =>
                          updateSettings({ ctaButtonColor: e.target.value })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Text Color
                      </label>
                      <input
                        type="color"
                        value={settings.ctaButtonTextColor}
                        onChange={(e) =>
                          updateSettings({ ctaButtonTextColor: e.target.value })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Background Color
                    </label>
                    <input
                      type="text"
                      value={settings.ctaBackgroundColor}
                      onChange={(e) =>
                        updateSettings({ ctaBackgroundColor: e.target.value })
                      }
                      placeholder="e.g., rgba(233, 92, 73, 0.05)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                </div>
              )}

              {activeTab === "features" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Features Title
                    </label>
                    <input
                      type="text"
                      value={settings.featuresTitle}
                      onChange={(e) =>
                        updateSettings({ featuresTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Features Subtitle
                    </label>
                    <textarea
                      value={settings.featuresSubtitle}
                      onChange={(e) =>
                        updateSettings({ featuresSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.featuresButtonText}
                      onChange={(e) =>
                        updateSettings({ featuresButtonText: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Color
                      </label>
                      <input
                        type="color"
                        value={settings.featuresButtonColor}
                        onChange={(e) =>
                          updateSettings({
                            featuresButtonColor: e.target.value,
                          })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Text Color
                      </label>
                      <input
                        type="color"
                        value={settings.featuresButtonTextColor}
                        onChange={(e) =>
                          updateSettings({
                            featuresButtonTextColor: e.target.value,
                          })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "featurescards" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Features Cards
                  </h3>
                  {settings.featuresCards.map((card, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Card {index + 1}
                        </h4>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Card Name
                          </label>
                          <input
                            type="text"
                            value={card.name}
                            onChange={(e) => {
                              const newCards = [...settings.featuresCards];
                              newCards[index] = {
                                ...newCards[index],
                                name: e.target.value,
                              };
                              updateSettings({ featuresCards: newCards });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Card Description
                          </label>
                          <textarea
                            value={card.description}
                            onChange={(e) => {
                              const newCards = [...settings.featuresCards];
                              newCards[index] = {
                                ...newCards[index],
                                description: e.target.value,
                              };
                              updateSettings({ featuresCards: newCards });
                            }}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "howitworks" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      How It Works Title
                    </label>
                    <input
                      type="text"
                      value={settings.howItWorksTitle}
                      onChange={(e) =>
                        updateSettings({ howItWorksTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      How It Works Subtitle
                    </label>
                    <textarea
                      value={settings.howItWorksSubtitle}
                      onChange={(e) =>
                        updateSettings({ howItWorksSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Video Section Title
                    </label>
                    <input
                      type="text"
                      value={settings.howItWorksVideoTitle}
                      onChange={(e) =>
                        updateSettings({ howItWorksVideoTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Video Section Subtitle
                    </label>
                    <textarea
                      value={settings.howItWorksVideoSubtitle}
                      onChange={(e) =>
                        updateSettings({
                          howItWorksVideoSubtitle: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                </div>
              )}

              {activeTab === "howitworkssteps" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    How It Works Steps
                  </h3>
                  {settings.howItWorksSteps.map((step, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Step {step.step}
                        </h4>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Step Number
                          </label>
                          <input
                            type="text"
                            value={step.step}
                            onChange={(e) => {
                              const newSteps = [...settings.howItWorksSteps];
                              newSteps[index] = {
                                ...newSteps[index],
                                step: e.target.value,
                              };
                              updateSettings({ howItWorksSteps: newSteps });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Step Title
                          </label>
                          <input
                            type="text"
                            value={step.title}
                            onChange={(e) => {
                              const newSteps = [...settings.howItWorksSteps];
                              newSteps[index] = {
                                ...newSteps[index],
                                title: e.target.value,
                              };
                              updateSettings({ howItWorksSteps: newSteps });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Step Description
                          </label>
                          <textarea
                            value={step.description}
                            onChange={(e) => {
                              const newSteps = [...settings.howItWorksSteps];
                              newSteps[index] = {
                                ...newSteps[index],
                                description: e.target.value,
                              };
                              updateSettings({ howItWorksSteps: newSteps });
                            }}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "partners" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Partners Section Title
                    </label>
                    <input
                      type="text"
                      value={settings.partnersTitle || "Partner with de5.ai"}
                      onChange={(e) =>
                        updateSettings({ partnersTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Partners Section Subtitle
                    </label>
                    <textarea
                      value={
                        settings.partnersSubtitle ||
                        "Join our ecosystem of forward-thinking partners and unlock new revenue streams while delivering cutting-edge DeFi automation solutions to your customers."
                      }
                      onChange={(e) =>
                        updateSettings({ partnersSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Partnership Contact Email
                    </label>
                    <input
                      id="partnership-email"
                      type="email"
                      value={settings.partnershipEmail || "partnerships@de5.ai"}
                      onChange={(e) =>
                        updateSettings({ partnershipEmail: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                </div>
              )}

              {activeTab === "institutions" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Institutions Title
                    </label>
                    <input
                      type="text"
                      value={settings.institutionsTitle || "For Institutions"}
                      onChange={(e) =>
                        updateSettings({ institutionsTitle: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Institutions Subtitle
                    </label>
                    <textarea
                      value={
                        settings.institutionsSubtitle ||
                        "Leverage the power of DeFi automation for your institutional clients. Our platform is designed to meet the highest security and compliance standards."
                      }
                      onChange={(e) =>
                        updateSettings({ institutionsSubtitle: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.institutionsButtonText || "Learn More"}
                      onChange={(e) =>
                        updateSettings({
                          institutionsButtonText: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rebalancing Description
                    </label>
                    <textarea
                      value={
                        settings.institutionsRebalancingDescription ||
                        "Our rebalancing engine ensures your portfolio stays aligned with your strategic goals, even in volatile markets."
                      }
                      onChange={(e) =>
                        updateSettings({
                          institutionsRebalancingDescription: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Security Description
                    </label>
                    <textarea
                      value={
                        settings.institutionsSecurityDescription ||
                        "Built on top of the most secure DeFi protocols, your assets are protected by cutting-edge security measures."
                      }
                      onChange={(e) =>
                        updateSettings({
                          institutionsSecurityDescription: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Asset Description
                    </label>
                    <textarea
                      value={
                        settings.institutionsAssetDescription ||
                        "Access a wide range of DeFi assets, including stablecoins, tokens, and NFTs, all managed with precision and transparency."
                      }
                      onChange={(e) =>
                        updateSettings({
                          institutionsAssetDescription: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                </div>
              )}

              {activeTab === "institutionsfeatures" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Institutions Features
                  </h3>
                  {settings.institutionsFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Feature {index + 1}
                        </h4>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Feature Name
                          </label>
                          <input
                            type="text"
                            value={feature.name}
                            onChange={(e) => {
                              const newFeatures = [
                                ...settings.institutionsFeatures,
                              ];
                              newFeatures[index] = {
                                ...newFeatures[index],
                                name: e.target.value,
                              };
                              updateSettings({
                                institutionsFeatures: newFeatures,
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Feature Description
                          </label>
                          <textarea
                            value={feature.description}
                            onChange={(e) => {
                              const newFeatures = [
                                ...settings.institutionsFeatures,
                              ];
                              newFeatures[index] = {
                                ...newFeatures[index],
                                description: e.target.value,
                              };
                              updateSettings({
                                institutionsFeatures: newFeatures,
                              });
                            }}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                            style={
                              {
                                "--focus-ring-color": settings.primaryColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) =>
                              (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                            }
                            onBlur={(e) =>
                              (e.currentTarget.style.boxShadow = "")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "global" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Primary Color
                    </label>
                    <input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) =>
                        updateSettings({ primaryColor: e.target.value })
                      }
                      className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Secondary Color
                    </label>
                    <input
                      type="color"
                      value={settings.secondaryColor}
                      onChange={(e) =>
                        updateSettings({ secondaryColor: e.target.value })
                      }
                      className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Logo Color
                    </label>
                    <input
                      type="color"
                      value={settings.logoColor}
                      onChange={(e) =>
                        updateSettings({ logoColor: e.target.value })
                      }
                      className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Header Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.headerButtonText}
                      onChange={(e) =>
                        updateSettings({ headerButtonText: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent"
                      style={
                        {
                          "--focus-ring-color": settings.primaryColor,
                        } as React.CSSProperties
                      }
                      onFocus={(e) =>
                        (e.currentTarget.style.boxShadow = `0 0 0 2px ${settings.primaryColor}`)
                      }
                      onBlur={(e) => (e.currentTarget.style.boxShadow = "")}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Header Button Color
                      </label>
                      <input
                        type="color"
                        value={settings.headerButtonColor}
                        onChange={(e) =>
                          updateSettings({ headerButtonColor: e.target.value })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Header Button Text Color
                      </label>
                      <input
                        type="color"
                        value={settings.headerButtonTextColor}
                        onChange={(e) =>
                          updateSettings({
                            headerButtonTextColor: e.target.value,
                          })
                        }
                        className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "subscriptions" && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Email Subscriptions ({subscriptions.length})
                    </h3>
                    <motion.button
                      onClick={fetchSubscriptions}
                      disabled={subscriptionsLoading}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                      whileHover={{ scale: 1.05 }}
                    >
                      <ArrowPathIcon
                        className={`w-4 h-4 ${
                          subscriptionsLoading ? "animate-spin" : ""
                        }`}
                      />
                      <span>Refresh</span>
                    </motion.button>
                  </div>

                  {subscriptionsLoading ? (
                    <div className="text-center py-8">
                      <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
                        style={{ borderColor: settings.primaryColor }}
                      ></div>
                      <p className="text-gray-600 dark:text-gray-300">
                        Loading subscriptions...
                      </p>
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div className="text-center py-8">
                      <EnvelopeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300">
                        No email subscriptions yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {subscriptions.map((subscription) => (
                        <div
                          key={subscription.id}
                          className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                        >
                          <div className="flex items-center space-x-3">
                            <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {subscription.email}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Subscribed on{" "}
                                {new Date(
                                  subscription.subscribed_at
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <motion.button
                            onClick={() =>
                              handleUnsubscribe(subscription.email)
                            }
                            className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Unsubscribe</span>
                          </motion.button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "waitlist" && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Waitlist Entries ({waitlist.length})
                    </h3>
                    <motion.button
                      onClick={fetchWaitlist}
                      disabled={waitlistLoading}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                      whileHover={{ scale: 1.05 }}
                    >
                      <ArrowPathIcon
                        className={`w-4 h-4 ${
                          waitlistLoading ? "animate-spin" : ""
                        }`}
                      />
                      <span>Refresh</span>
                    </motion.button>
                  </div>

                  {waitlistLoading ? (
                    <div className="text-center py-8">
                      <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
                        style={{ borderColor: settings.primaryColor }}
                      ></div>
                      <p className="text-gray-600 dark:text-gray-300">
                        Loading waitlist...
                      </p>
                    </div>
                  ) : waitlist.length === 0 ? (
                    <div className="text-center py-8">
                      <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300">
                        No waitlist entries yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {waitlist.map((entry) => (
                        <div
                          key={entry.id}
                          className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <UserGroupIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {entry.email}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Joined on{" "}
                                  {new Date(
                                    entry.created_at
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <motion.button
                              onClick={() =>
                                handleDeleteWaitlistEntry(entry.email)
                              }
                              className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <TrashIcon className="w-4 h-4" />
                              <span>Delete</span>
                            </motion.button>
                          </div>
                          <div className="space-y-2 ml-8">
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Telegram ID:
                              </p>
                              <p className="text-sm text-gray-900 dark:text-white">
                                {entry.telegram_id}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Automation Goal:
                              </p>
                              <p className="text-sm text-gray-900 dark:text-white">
                                {entry.automation_goal}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <motion.button
                onClick={handleSave}
                className="w-full mt-6 py-3 px-4 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                style={
                  {
                    backgroundColor: settings.primaryColor,
                    "--hover-color": settings.primaryColor,
                  } as React.CSSProperties
                }
                onMouseEnter={(e) => {
                  const color = settings.primaryColor;
                  const rgb = color.match(/\d+/g);
                  if (rgb) {
                    const r = parseInt(rgb[0]);
                    const g = parseInt(rgb[1]);
                    const b = parseInt(rgb[2]);
                    e.currentTarget.style.backgroundColor = `rgb(${Math.max(
                      0,
                      r - 20
                    )}, ${Math.max(0, g - 20)}, ${Math.max(0, b - 20)})`;
                  }
                }}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    settings.primaryColor)
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CheckIcon className="w-5 h-5" />
                <span>Save Changes</span>
              </motion.button>
            </div>

            {/* Preview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Live Preview
              </h2>
              <div className="space-y-4">
                {activeTab === "hero" && (
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: settings.heroBackgroundColor }}
                  >
                    <h3
                      className="text-2xl font-oxanium font-bold mb-2"
                      style={{ color: settings.primaryColor }}
                    >
                      {settings.heroTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {settings.heroSubtitle}
                    </p>
                    <button
                      className="px-6 py-3 rounded-lg font-semibold"
                      style={{
                        backgroundColor: settings.heroButtonColor,
                        color: settings.heroButtonTextColor,
                      }}
                    >
                      {settings.heroButtonText}
                    </button>
                  </div>
                )}

                {activeTab === "cta" && (
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: settings.ctaBackgroundColor }}
                  >
                    <h3
                      className="text-2xl font-oxanium font-bold mb-2"
                      style={{ color: settings.primaryColor }}
                    >
                      {settings.ctaTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {settings.ctaSubtitle}
                    </p>
                    <button
                      className="px-6 py-3 rounded-lg font-semibold"
                      style={{
                        backgroundColor: settings.ctaButtonColor,
                        color: settings.ctaButtonTextColor,
                      }}
                    >
                      {settings.ctaButtonText}
                    </button>
                  </div>
                )}

                {activeTab === "features" && (
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: "rgba(249, 250, 251, 0.8)" }}
                  >
                    <h3
                      className="text-2xl font-oxanium font-bold mb-2"
                      style={{ color: settings.primaryColor }}
                    >
                      {settings.featuresTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {settings.featuresSubtitle}
                    </p>
                    <button
                      className="px-6 py-3 rounded-lg font-semibold"
                      style={{
                        backgroundColor: settings.featuresButtonColor,
                        color: settings.featuresButtonTextColor,
                      }}
                    >
                      {settings.featuresButtonText}
                    </button>
                  </div>
                )}

                {activeTab === "featurescards" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Features Cards Preview
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {settings.featuresCards.slice(0, 4).map((card, index) => (
                        <div
                          key={index}
                          className="p-4 border rounded-lg bg-white dark:bg-gray-800"
                        >
                          <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                            {card.name}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {card.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "howitworks" && (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3
                        className="text-2xl font-oxanium font-bold mb-2"
                        style={{ color: settings.primaryColor }}
                      >
                        {settings.howItWorksTitle}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        {settings.howItWorksSubtitle}
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4
                        className="text-xl font-oxanium font-bold mb-2"
                        style={{ color: settings.primaryColor }}
                      >
                        {settings.howItWorksVideoTitle}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-300">
                        {settings.howItWorksVideoSubtitle}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === "howitworkssteps" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      How It Works Steps Preview
                    </h4>
                    <div className="space-y-4">
                      {settings.howItWorksSteps.map((step, index) => (
                        <div
                          key={index}
                          className="p-4 border rounded-lg bg-white dark:bg-gray-800"
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                              style={{
                                backgroundColor: settings.primaryColor,
                                color: "white",
                              }}
                            >
                              {step.step}
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {step.title}
                              </h5>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "partners" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Partners Section Preview
                    </h4>
                    <div className="p-6 border rounded-lg bg-white dark:bg-gray-800">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-oxanium font-bold mb-2">
                          <span className="text-gray-900 dark:text-white">
                            Partner with
                          </span>
                          <br />
                          <span
                            className="bg-gradient-to-r from-transparent via-transparent to-transparent bg-clip-text text-transparent"
                            style={{
                              backgroundImage: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.logoColor})`,
                            }}
                          >
                            de5.ai
                          </span>
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {settings.partnersSubtitle ||
                            "Join our ecosystem of forward-thinking partners and unlock new revenue streams while delivering cutting-edge DeFi automation solutions to your customers."}
                        </p>
                      </div>
                      <div className="text-center">
                        <a
                          href={`mailto:${
                            settings.partnershipEmail || "partnerships@de5.ai"
                          }?subject=Partnership Inquiry`}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-oxanium font-semibold text-white transition-all duration-300"
                          style={{
                            backgroundColor: settings.primaryColor,
                            boxShadow: `0 4px 14px 0 ${settings.primaryColor}25`,
                          }}
                        >
                          <UserGroupIcon className="h-5 w-5" />
                          Start Partnership Discussion
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "institutions" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Institutions Section Preview
                    </h4>
                    <div className="p-6 border rounded-lg bg-white dark:bg-gray-800">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-oxanium font-bold mb-2">
                          <span className="text-gray-900 dark:text-white">
                            For Institutions
                          </span>
                          <br />
                          <span
                            className="bg-gradient-to-r from-transparent via-transparent to-transparent bg-clip-text text-transparent"
                            style={{
                              backgroundImage: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.logoColor})`,
                            }}
                          >
                            de5.ai
                          </span>
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {settings.institutionsSubtitle ||
                            "Leverage the power of DeFi automation for your institutional clients. Our platform is designed to meet the highest security and compliance standards."}
                        </p>
                      </div>
                      <div className="text-center">
                        <a
                          href={`mailto:${
                            settings.partnershipEmail || "partnerships@de5.ai"
                          }?subject=Institutional Partnership Inquiry`}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-oxanium font-semibold text-white transition-all duration-300"
                          style={{
                            backgroundColor: settings.primaryColor,
                            boxShadow: `0 4px 14px 0 ${settings.primaryColor}25`,
                          }}
                        >
                          <UserGroupIcon className="h-5 w-5" />
                          Start Institutional Partnership
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "institutionsfeatures" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Institutions Features Preview
                    </h4>
                    <div className="space-y-4">
                      {settings.institutionsFeatures.map((feature, index) => (
                        <div
                          key={index}
                          className="p-4 border rounded-lg bg-white dark:bg-gray-800"
                        >
                          <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                            {feature.name}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {feature.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "global" && (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: settings.logoColor }}
                      >
                        Logo Preview
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        de5.ai
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">Color Palette</h4>
                      <div className="flex space-x-4">
                        <div className="text-center">
                          <div
                            className="w-12 h-12 rounded-lg border"
                            style={{ backgroundColor: settings.primaryColor }}
                          />
                          <p className="text-xs mt-1">Primary</p>
                        </div>
                        <div className="text-center">
                          <div
                            className="w-12 h-12 rounded-lg border"
                            style={{ backgroundColor: settings.secondaryColor }}
                          />
                          <p className="text-xs mt-1">Secondary</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "subscriptions" && (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">
                        Newsletter Signup Preview
                      </h4>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            Stay updated:
                          </span>
                          <div className="flex gap-2">
                            <input
                              id="preview-email"
                              type="email"
                              placeholder="Enter your email"
                              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-48"
                              disabled
                            />
                            <button
                              className="px-4 py-2 rounded-lg font-medium text-sm text-white"
                              style={{ backgroundColor: settings.primaryColor }}
                              disabled
                            >
                              Subscribe
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                        Statistics
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: settings.primaryColor }}
                          >
                            {subscriptions.length}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            Total Subscribers
                          </p>
                        </div>
                        <div className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: settings.primaryColor }}
                          >
                            {
                              subscriptions.filter((sub) => {
                                const subDate = new Date(sub.subscribed_at);
                                const today = new Date();
                                const diffTime = Math.abs(
                                  today.getTime() - subDate.getTime()
                                );
                                const diffDays = Math.ceil(
                                  diffTime / (1000 * 60 * 60 * 24)
                                );
                                return diffDays <= 7;
                              }).length
                            }
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            This Week
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "waitlist" && (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                        Waitlist Statistics
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: settings.primaryColor }}
                          >
                            {waitlist.length}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            Total Entries
                          </p>
                        </div>
                        <div className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: settings.primaryColor }}
                          >
                            {
                              waitlist.filter((entry) => {
                                const entryDate = new Date(entry.created_at);
                                const today = new Date();
                                const diffTime = Math.abs(
                                  today.getTime() - entryDate.getTime()
                                );
                                const diffDays = Math.ceil(
                                  diffTime / (1000 * 60 * 60 * 24)
                                );
                                return diffDays <= 7;
                              }).length
                            }
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            This Week
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">
                        Waitlist Form Preview
                      </h4>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          Users will see a modal with fields for:
                        </p>
                        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                          <li className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: settings.primaryColor }}
                            />
                            Email address
                          </li>
                          <li className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: settings.primaryColor }}
                            />
                            Automation goals
                          </li>
                          <li className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: settings.primaryColor }}
                            />
                            Telegram ID
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 z-50"
            >
              <CheckIcon className="w-5 h-5" />
              <span>Settings saved successfully!</span>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
